import { AuthEvent } from '@/features/Auth/machine/states'
import { retrieve } from '@/services/api/session/views'

// `profiles/i/` is IsAuthenticated, and the backend resolves a token whose uid matches no
// Profile to an unauthenticated request, so "this server has never seen you" arrives as a
// 401/403/404. Every other outcome means the lookup itself failed and says nothing about
// whether the person is registered — reading those as "new user" is what routed registered
// people into signup on a 5xx or a dropped connection. See EPIC-004.
const PROFILE_UNKNOWN_STATUSES = [401, 403, 404]

export type Emit = (event: AuthEvent) => void

export async function resolveProfile(emit: Emit): Promise<void> {
  try {
    await retrieve()

    // Existence only. Whether the profile is *complete* is a different question, asked by
    // `ensureProfileCompletion` when an action needs the answer — reviewing, publishing a
    // story, finishing a lesson. Asking it here turned every sign-in into a demand for
    // details the product only wants at those moments.
    emit({ type: 'profileResolved' })
  } catch (error) {
    // Axios puts the status on the response; a request that never reached the server has no
    // response at all, and that is exactly the case that must not read as "no profile here".
    const status = (error as { response?: { status?: number } })?.response?.status

    if (PROFILE_UNKNOWN_STATUSES.includes(status)) {
      emit({ type: 'profileAbsent' })
      return
    }

    emit({ type: 'failed', reason: 'unavailable' })
  }
}
